import { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Button, Container, Divider, Group, Paper, Select, SimpleGrid, Text, TextInput } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { DataTable } from "mantine-datatable";
import { IconAlertCircle, IconCheck, IconUpload } from "@tabler/icons-react";
import HttpClient from "../helpers/HttpClient";
import { config } from "../helpers/Config";
import { getAccessToken } from "../helpers/TokenRefresh";
import axios from "axios";
import { BookingType } from "../types/BookingModel";
import { PaymentAccount } from "../types/payment-account.type";
import dayjs from "dayjs";

interface ParsedCsvRow {
  _key: number; // stable index used as idAccessor for mantine-datatable row selection
  date: string;
  description: string;
  amount: number;
  extractedName: string;
  extractedIban: string;
  bankReference: string;
  personId?: number;
  personName?: string;
}

interface ImportCsvResult {
  matched: ParsedCsvRow[];
  unmatched: ParsedCsvRow[];
  alreadyImported: ParsedCsvRow[];
  period?: { from: string; to: string } | null;
}

// One recorded import (server: bank-import) — which account, which period.
interface BankImport {
  id: number;
  createdAt: string;
  paymentAccountId: number | null;
  accountName: string;
  fileName: string;
  periodFrom: string | null;
  periodTo: string | null;
  rowCount: number;
  bookedCount: number;
  alreadyImportedCount: number;
  unmatchedCount: number;
  importedByName: string;
}

interface AccountStatus {
  account: { id: number; name: string; iban: string };
  lastImport: BankImport | null;
}

const isoDe = (iso?: string | null) => (iso ? dayjs(iso).format("DD.MM.YYYY") : "–");

function parseCsvDate(dateStr: string): Date {
  const [day, month, year] = dateStr.split(".");
  return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
}

function formatEur(amount: number) {
  return amount.toLocaleString("de-DE", { style: "currency", currency: "EUR" });
}

function ImportPayments() {
  const fileRef = useRef<HTMLInputElement>(null);
  const [loading, setLoading] = useState(false);
  const [booking, setBooking] = useState(false);
  const [result, setResult] = useState<ImportCsvResult | null>(null);
  const [bookingTypes, setBookingTypes] = useState<{ value: string; label: string }[]>([]);

  // Which bank account the CSV comes from + the import history per account.
  const [accounts, setAccounts] = useState<PaymentAccount[]>([]);
  const [accountId, setAccountId] = useState<string | null>(null);
  const [accountStatus, setAccountStatus] = useState<AccountStatus[]>([]);
  const [history, setHistory] = useState<BankImport[]>([]);
  const [fileName, setFileName] = useState("");
  const [overlaps, setOverlaps] = useState<BankImport[]>([]);

  // Outgoing (negative amounts = Zahlungsausgang)
  const [selectedOutgoing, setSelectedOutgoing] = useState<ParsedCsvRow[]>([]);
  const [bookingTypeOutId, setBookingTypeOutId] = useState<string | null>(null);
  const [bookingTextOut, setBookingTextOut] = useState("CSV Import");

  // Incoming (positive amounts = Zahlungseingang)
  const [selectedIncoming, setSelectedIncoming] = useState<ParsedCsvRow[]>([]);
  const [bookingTypeInId, setBookingTypeInId] = useState<string | null>(null);
  const [bookingTextIn, setBookingTextIn] = useState("CSV Import");

  useEffect(() => {
    new HttpClient<BookingType>("/bookingType").getAll().then((types) => {
      setBookingTypes(types.map((t) => ({ value: t.id.toString(), label: t.title })));
    });
    new HttpClient<PaymentAccount>("/payment-account").getAll().then((list) => {
      setAccounts(list);
      const preset = list.find((a) => a.isDefault) ?? (list.length === 1 ? list[0] : undefined);
      if (preset) setAccountId(String(preset.id));
    }).catch(() => {});
  }, []);

  const loadStatus = useCallback(() => {
    new HttpClient<AccountStatus>("/bank-import/latest").getAll().then(setAccountStatus).catch(() => {});
  }, []);

  const loadHistory = useCallback(() => {
    if (!accountId) { setHistory([]); return; }
    new HttpClient<BankImport>("/bank-import").getAll({ paymentAccountId: accountId, take: "20" })
      .then(setHistory).catch(() => setHistory([]));
  }, [accountId]);

  useEffect(() => { loadStatus(); }, [loadStatus]);
  useEffect(() => { loadHistory(); }, [loadHistory]);

  // A loaded file belongs to the account chosen at load time.
  const changeAccount = (id: string | null) => {
    setAccountId(id);
    setResult(null);
    setOverlaps([]);
    setSelectedOutgoing([]);
    setSelectedIncoming([]);
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !accountId) return;
    setLoading(true);
    setFileName(file.name);
    setOverlaps([]);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const token = await getAccessToken();
      const res = await axios.post<ImportCsvResult>(
        `${config.apiUrl}/booking/parse-csv`,
        formData,
        { headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}), "Content-Type": "multipart/form-data" } }
      );
      // Stamp each row with a stable index so DataTable can identify them for selection.
      let idx = 0;
      const stamp = (rows: typeof res.data.matched) => rows.map((r) => ({ ...r, _key: idx++ }));
      const matched = stamp(res.data.matched);
      const unmatched = stamp(res.data.unmatched);
      const alreadyImported = stamp(res.data.alreadyImported ?? []);
      const period = res.data.period ?? null;
      setResult({ matched, unmatched, alreadyImported, period });
      if (period) {
        new HttpClient<BankImport>("/bank-import/overlaps")
          .getAll({ paymentAccountId: accountId, from: period.from, to: period.to })
          .then(setOverlaps).catch(() => {});
      }
      setSelectedOutgoing(matched.filter((r) => r.amount < 0));
      setSelectedIncoming(matched.filter((r) => r.amount > 0));
    } catch (err: any) {
      const msg = err?.response?.status === 401
        ? "Sitzung abgelaufen, bitte neu anmelden."
        : "CSV konnte nicht gelesen werden.";
      notifications.show({ color: "red", title: "Fehler", message: msg });
    } finally {
      setLoading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const bookRows = async (rows: ParsedCsvRow[], bookingTypeId: string, bookingText: string) => {
    const bookings = rows.map((row) => ({
      bookingDate: parseCsvDate(row.date),
      bookingText: bookingText || row.extractedName,
      bookingAmount: Math.abs(row.amount),
      person: { id: row.personId },
      bookingType: { id: parseInt(bookingTypeId) },
      bankReference: row.bankReference,
    }));
    // Route through HttpClient so the 401 → token-refresh → retry interceptor applies.
    const res: any = await new HttpClient<any>('/booking/batch').post(bookings as any);
    const results: { skipped?: boolean }[] = res?.data ?? [];
    const skipped = results.filter((r) => r.skipped).length;
    return { created: bookings.length - skipped, skipped };
  };

  // Remembers the import (account, period, counts) for the history. A failure
  // here doesn't undo anything — the bookings are already saved.
  const recordImport = async (booked: number, skipped: number) => {
    if (!result || !accountId) return;
    try {
      await new HttpClient<Record<string, unknown>>("/bank-import").post({
        paymentAccountId: Number(accountId),
        fileName,
        periodFrom: result.period?.from,
        periodTo: result.period?.to,
        rowCount: result.matched.length + result.unmatched.length + result.alreadyImported.length,
        bookedCount: booked,
        alreadyImportedCount: result.alreadyImported.length + skipped,
        unmatchedCount: result.unmatched.length,
      });
      loadStatus();
      loadHistory();
    } catch {
      notifications.show({ color: "orange", message: "Buchungen gespeichert, aber der Import konnte nicht im Verlauf vermerkt werden." });
    }
  };

  const handleBook = async () => {
    const hasOutgoing = selectedOutgoing.length > 0 && bookingTypeOutId;
    const hasIncoming = selectedIncoming.length > 0 && bookingTypeInId;
    if (!hasOutgoing && !hasIncoming) return;
    setBooking(true);
    try {
      let created = 0;
      let skipped = 0;
      if (hasOutgoing) {
        const r = await bookRows(selectedOutgoing, bookingTypeOutId!, bookingTextOut);
        created += r.created;
        skipped += r.skipped;
      }
      if (hasIncoming) {
        const r = await bookRows(selectedIncoming, bookingTypeInId!, bookingTextIn);
        created += r.created;
        skipped += r.skipped;
      }
      const message = skipped > 0
        ? `${created} Buchung(en) erstellt, ${skipped} als Duplikat(e) übersprungen.`
        : `${created} Buchungen wurden erstellt.`;
      notifications.show({ color: "green", title: "Gespeichert", message });
      await recordImport(created, skipped);
      setResult(null);
      setOverlaps([]);
      setSelectedOutgoing([]);
      setSelectedIncoming([]);
    } catch {
      notifications.show({ color: "red", title: "Fehler", message: "Buchungen konnten nicht gespeichert werden." });
    } finally {
      setBooking(false);
    }
  };

  const matchedOutgoing = result?.matched.filter((r) => r.amount < 0) ?? [];
  const matchedIncoming = result?.matched.filter((r) => r.amount > 0) ?? [];
  const unmatchedRows = result?.unmatched ?? [];
  const alreadyImportedRows = result?.alreadyImported ?? [];

  const outgoingTotal = selectedOutgoing.reduce((s, r) => s + Math.abs(r.amount), 0);
  const incomingTotal = selectedIncoming.reduce((s, r) => s + Math.abs(r.amount), 0);
  const unmatchedTotal = unmatchedRows.reduce((s, r) => s + Math.abs(r.amount), 0);
  const alreadyImportedTotal = alreadyImportedRows.reduce((s, r) => s + Math.abs(r.amount), 0);

  const canBook =
    (selectedOutgoing.length > 0 && !!bookingTypeOutId) ||
    (selectedIncoming.length > 0 && !!bookingTypeInId);
  const totalToBook = selectedOutgoing.length + selectedIncoming.length;

  const amountCol = (sign: 'abs' | 'signed' = 'abs') => ({
    accessor: "amount" as const,
    title: "Betrag",
    textAlign: "right" as const,
    width: 130,
    render: ({ amount }: ParsedCsvRow) =>
      sign === 'abs'
        ? formatEur(Math.abs(amount))
        : formatEur(amount),
  });

  return (
    <Container fluid>
      <Group gap={16} mb="md">
        <h1>Zahlungen importieren</h1>
      </Group>

      {/* Up to which date each account has been imported */}
      {accountStatus.length > 0 && (
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} mb="md">
          {accountStatus.map(({ account, lastImport }) => (
            <Paper key={account.id} withBorder p="sm" radius="sm">
              <Text fw={600}>{account.name}</Text>
              <Text size="xs" c="dimmed" mb={4}>{account.iban}</Text>
              {lastImport ? (
                <>
                  <Text size="sm">Importiert bis <strong>{isoDe(lastImport.periodTo)}</strong></Text>
                  <Text size="xs" c="dimmed">
                    am {dayjs(lastImport.createdAt).format("DD.MM.YYYY")} von {lastImport.importedByName}
                  </Text>
                </>
              ) : (
                <Text size="sm" c="dimmed">Noch kein Import</Text>
              )}
            </Paper>
          ))}
        </SimpleGrid>
      )}

      <Group mb="lg" align="flex-end">
        <Select
          label="Konto"
          placeholder="Zahlungskonto wählen"
          data={accounts.map((a) => ({ value: String(a.id), label: `${a.name} (${a.iban})` }))}
          value={accountId}
          onChange={changeAccount}
          style={{ width: 340 }}
          required
        />
        <Button
          leftSection={<IconUpload size={16} />}
          loading={loading}
          disabled={!accountId}
          onClick={() => fileRef.current?.click()}
        >
          CSV-Datei laden
        </Button>
        <input ref={fileRef} type="file" accept=".csv" style={{ display: "none" }} onChange={handleFileChange} />
      </Group>

      {result && (
        <Text size="sm" mb="sm">
          Datei <strong>{fileName}</strong>
          {result.period ? <> – Zeitraum {isoDe(result.period.from)} bis {isoDe(result.period.to)}</> : null}
        </Text>
      )}

      {result && overlaps.length > 0 && (
        <Alert icon={<IconAlertCircle size={16} />} color="yellow" mb="md" title="Zeitraum überschneidet sich mit früheren Importen">
          {overlaps.map((o) => (
            <Text size="sm" key={o.id}>
              {isoDe(o.periodFrom)} – {isoDe(o.periodTo)} ({o.fileName}, am {dayjs(o.createdAt).format("DD.MM.YYYY")} von {o.importedByName})
            </Text>
          ))}
          <Text size="sm" mt="xs">
            Bereits gebuchte Zahlungen werden erkannt und übersprungen (siehe „bereits importiert“).
          </Text>
        </Alert>
      )}

      {result && (
        <>
          {/* Zahlungsausgang (outgoing) */}
          {matchedOutgoing.length > 0 && (
            <>
              <Text fw={600} mb="xs">Zahlungsausgang — zugeordnet ({matchedOutgoing.length})</Text>
              <DataTable
                withTableBorder borderRadius="sm" withColumnBorders striped highlightOnHover mb="xs"
                idAccessor="_key"
                records={matchedOutgoing}
                selectedRecords={selectedOutgoing}
                onSelectedRecordsChange={setSelectedOutgoing}
                columns={[
                  { accessor: "date", title: "Datum", width: 110 },
                  { accessor: "extractedName", title: "Empfänger (CSV)" },
                  { accessor: "personName", title: "Person" },
                  { accessor: "extractedIban", title: "IBAN", width: 200 },
                  amountCol(),
                ]}
              />
              <Group justify="space-between" mb="md" align="flex-end">
                <Group align="flex-end">
                  <Select
                    label="Buchungsart (Ausgang)"
                    placeholder="Buchungsart wählen"
                    data={bookingTypes}
                    value={bookingTypeOutId}
                    onChange={setBookingTypeOutId}
                    style={{ width: 260 }}
                    required
                  />
                  <TextInput
                    label="Buchungstext"
                    value={bookingTextOut}
                    onChange={(e) => setBookingTextOut(e.currentTarget.value)}
                    style={{ width: 220 }}
                  />
                </Group>
                <Text fw={500}>
                  Ausgewählt ({selectedOutgoing.length}): {formatEur(outgoingTotal)}
                </Text>
              </Group>
            </>
          )}

          {/* Zahlungseingang (incoming) */}
          {matchedIncoming.length > 0 && (
            <>
              <Text fw={600} mb="xs">Zahlungseingang — zugeordnet ({matchedIncoming.length})</Text>
              <DataTable
                withTableBorder borderRadius="sm" withColumnBorders striped highlightOnHover mb="xs"
                idAccessor="_key"
                records={matchedIncoming}
                selectedRecords={selectedIncoming}
                onSelectedRecordsChange={setSelectedIncoming}
                columns={[
                  { accessor: "date", title: "Datum", width: 110 },
                  { accessor: "extractedName", title: "Absender (CSV)" },
                  { accessor: "personName", title: "Person" },
                  { accessor: "extractedIban", title: "IBAN", width: 200 },
                  amountCol(),
                ]}
              />
              <Group justify="space-between" mb="md" align="flex-end">
                <Group align="flex-end">
                  <Select
                    label="Buchungsart (Eingang)"
                    placeholder="Buchungsart wählen"
                    data={bookingTypes}
                    value={bookingTypeInId}
                    onChange={setBookingTypeInId}
                    style={{ width: 260 }}
                    required
                  />
                  <TextInput
                    label="Buchungstext"
                    value={bookingTextIn}
                    onChange={(e) => setBookingTextIn(e.currentTarget.value)}
                    style={{ width: 220 }}
                  />
                </Group>
                <Text fw={500}>
                  Ausgewählt ({selectedIncoming.length}): {formatEur(incomingTotal)}
                </Text>
              </Group>
            </>
          )}

          {/* Unmatched */}
          {unmatchedRows.length > 0 && (
            <>
              <Alert icon={<IconAlertCircle size={16} />} color="orange" mb="xs">
                {unmatchedRows.length} Zahlungen konnten keiner Person zugeordnet werden — Gesamtbetrag:{" "}
                <strong>{formatEur(unmatchedTotal)}</strong>
              </Alert>
              <DataTable
                withTableBorder borderRadius="sm" withColumnBorders mb="lg"
                records={unmatchedRows}
                columns={[
                  { accessor: "date", title: "Datum", width: 110 },
                  { accessor: "extractedName", title: "Empfänger/Absender (CSV)" },
                  { accessor: "description", title: "Beschreibung" },
                  amountCol('signed'),
                ]}
              />
            </>
          )}

          {/* Already imported (occurrence-count duplicate of a previously booked row) */}
          {alreadyImportedRows.length > 0 && (
            <>
              <Alert icon={<IconAlertCircle size={16} />} color="gray" mb="xs">
                {alreadyImportedRows.length} Zahlung(en) wurden bereits importiert und werden übersprungen — Gesamtbetrag:{" "}
                <strong>{formatEur(alreadyImportedTotal)}</strong>
              </Alert>
              <DataTable
                withTableBorder borderRadius="sm" withColumnBorders mb="lg"
                records={alreadyImportedRows}
                columns={[
                  { accessor: "date", title: "Datum", width: 110 },
                  { accessor: "extractedName", title: "Empfänger/Absender (CSV)" },
                  { accessor: "personName", title: "Person" },
                  amountCol('signed'),
                ]}
              />
            </>
          )}

          {(matchedOutgoing.length > 0 || matchedIncoming.length > 0) && (
            <>
              <Divider my="lg" />
              <Group>
                <Button
                  leftSection={<IconCheck size={16} />}
                  loading={booking}
                  disabled={!canBook}
                  onClick={handleBook}
                >
                  {totalToBook} Buchungen anlegen
                </Button>
              </Group>
            </>
          )}
        </>
      )}

      {accountId && history.length > 0 && (
        <>
          <Divider my="lg" />
          <Text fw={600} mb="xs">Bisherige Importe für dieses Konto</Text>
          <DataTable
            withTableBorder borderRadius="sm" striped
            records={history}
            columns={[
              { accessor: "createdAt", title: "Importiert am", width: 150, render: (h) => dayjs(h.createdAt).format("DD.MM.YYYY HH:mm") },
              { accessor: "period", title: "Zeitraum", render: (h) => `${isoDe(h.periodFrom)} – ${isoDe(h.periodTo)}` },
              { accessor: "fileName", title: "Datei" },
              { accessor: "bookedCount", title: "Gebucht", textAlign: "right", width: 90 },
              { accessor: "alreadyImportedCount", title: "Bereits importiert", textAlign: "right", width: 140 },
              { accessor: "unmatchedCount", title: "Nicht zugeordnet", textAlign: "right", width: 140 },
              { accessor: "importedByName", title: "Von" },
            ]}
          />
        </>
      )}
    </Container>
  );
}

export default ImportPayments;
